import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { Divider, ListItem } from 'material-ui';
import { FormattedMessage } from 'react-intl';
import KFlatButton from 'components/KJRN/KFlatButton';
import convert from 'utils/dateTools';
import pallet from 'constants/colors';
import TMessage from './TMessage';
import TLoading from './TLoading';
import { getPMEmployeeAppraisals } from '../pmappraisals/api';
import { STATUS } from '../pmappraisals/appraisalEnums';

const { STARTED, FINISHED, NONE } = STATUS;

// Helper function to convert numeric status to string status (for old appraisals)
const convertStatus = (numericStatus) => {
  if (numericStatus === FINISHED) return 'Finished';
  if (numericStatus === STARTED) return 'Started';
  return 'None';
};

// Helper function to normalize old appraisal data
const normalizeOldAppraisal = appraisal => ({
  ...appraisal,
  type: 'old',
  PMEmployeeAppraisalID: appraisal.PMAppraisalID,
  Title: { IS: appraisal.PMFormTitle },
  DefaultLanguage: 'IS',
  ManagerEmployeeName: appraisal.ManagerName,
  EmployeeStatus: convertStatus(appraisal.EmployeeStatus),
  ManagerStatus: convertStatus(appraisal.ManagerStatus),
  FinalStatus: convertStatus(appraisal.FinalStatus),
});

const styles = {
  container: {
    width: 'calc(100% - 72px)',
    height: 'calc(100% - 26px)',
    paddingLeft: 36,
    paddingRight: 36,
    paddingTop: 18,
    paddingBottom: 8,
    display: 'flex',
    flexDirection: 'column',
  },
  action: {
    flex: '1 1 0',
    justifyContent: 'center',
    minHeight: 40,
    maxHeight: 40,
  },
  actionButton: {
    maxWidth: 'none',
    fontSize: 14,
  },
  appraisal: {
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 18,
    paddingBottom: 18,
  },
  secondary: {
    display: 'flex',
    alignItems: 'center',
    height: 40,
    justifyContent: 'space-between',
  },
  tag: {
    fontSize: 14,
    color: 'white',
    borderRadius: 4,
    padding: 6,
  },
};

const TPMAppraisals = (props, context) => {
  const [newAppraisals, setNewAppraisals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const EmployeeMasterID = Number(localStorage.EmployeeMasterID);

  const {
    Appraisal: { openAppraisals: oldAppraisals, loading: appraisalLoading },
  } = useSelector(state => state);

  // Fetch new appraisals from API
  useEffect(() => {
    const fetchNewAppraisals = async () => {
      try {
        setLoading(true);
        const newResponse = await getPMEmployeeAppraisals().catch(() => ({ data: [] }));

        const filtered = (newResponse.data || [])
          .map(a => ({ ...a, type: 'new' }))
          .filter(a => a.EmployeeDetailID === Number(localStorage.DefaultEmployeeDetailID));

        setNewAppraisals(filtered);
        setError(null);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchNewAppraisals();
  }, [EmployeeMasterID]);

  if (error) {
    return <TMessage type="error" message="tile.pmappraisals.error" />;
  }

  if (loading || appraisalLoading) {
    return <TLoading />;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Combine old appraisals from Redux with new appraisals from API
  const normalizedOldAppraisals = (oldAppraisals || []).map(normalizeOldAppraisal);
  const combined = [...newAppraisals, ...normalizedOldAppraisals];

  // Filter for non-expired appraisals and where at least one status is not finished
  const list = combined.filter((a) => {
    if (a.Expired) return false;
    // Check both numeric (new) and string (old) statuses
    const allFinished = (a.EmployeeStatus === FINISHED || a.EmployeeStatus === 'Finished') &&
      (a.ManagerStatus === FINISHED || a.ManagerStatus === 'Finished') &&
      (a.FinalStatus === FINISHED || a.FinalStatus === 'Finished');
    return !allFinished;
  });

  if (!list || !list.length) {
    return (
      <TMessage message="tile.appraisals.empty">
        <KFlatButton
          style={styles.actionButton}
          onClick={() => context.router.push('/pmappraisals')}
          containerStyle={styles.action}
          label={<FormattedMessage id="tile.appraisals.goto" />}
        />
      </TMessage>
    );
  }

  const getStatusTag = (appraisal) => {
    if (appraisal.EmployeeStatus === NONE || appraisal.EmployeeStatus === 'None') {
      return (
        <div style={{ ...styles.tag, backgroundColor: pallet.errorColor }}>
          <FormattedMessage id="tile.appraisals.start" />
        </div>
      );
    }
    if (appraisal.EmployeeStatus === STARTED || appraisal.EmployeeStatus === 'Started') {
      return (
        <div style={{ ...styles.tag, backgroundColor: pallet.warningColor }}>
          <FormattedMessage id="tile.appraisals.handin" />
        </div>
      );
    }
    return null;
  };

  return (
    <div style={styles.container}>
      {list.map(appr => (
        <div key={appr.PMEmployeeAppraisalID}>
          <ListItem
            className="pmappraisal-item"
            innerDivStyle={styles.appraisal}
            hoverColor="transparent"
            primaryText={get(appr, ['Title', appr.DefaultLanguage]) || 'Enginn titill'}
            secondaryText={
              <div style={styles.secondary}>
                {convert(appr.EmployeeDueDate)}
                {getStatusTag(appr)}
              </div>
            }
            onClick={() => {
              if (appr.type === 'old') {
                context.router.push(`/appraisals/${appr.PMAppraisalID}`);
              } else {
                context.router.push(`/pmappraisals/${appr.PMEmployeeAppraisalID}`);
              }
            }}
          />
          <Divider />
        </div>
      ))}

      <KFlatButton
        style={styles.actionButton}
        onClick={() => context.router.push('/pmappraisals')}
        containerStyle={styles.action}
        label={<FormattedMessage id="tile.appraisals.goto" />}
      />
    </div>
  );
};

TPMAppraisals.contextTypes = {
  router: PropTypes.object.isRequired,
};

export default TPMAppraisals;



// WEBPACK FOOTER //
// ./src/containers/tiles/TPMAppraisals.js